import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Loader2, Users, Grid3x3, MessageSquare, Flame, ChevronDown, ChevronUp, HelpCircle,
  Stethoscope, AlertTriangle, ExternalLink, Info, CheckCircle2, Tags, Repeat, Clock, Printer, Package, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { hasPersonas } from '../config/aiVisibilityBrands';
import AiBrandLogo from '../components/AiBrandLogo';
import {
  API, PLATFORM_LABEL, PLATFORM_ORDER, PlatformLogo, Card, CATEGORY_LABEL,
} from './YatasAiVisibility';

/**
 * AI Visibility · Personalar — derma-tr projesi: 10 kurgusal kullanıcı (persona) ChatGPT, Gemini ve
 * Claude ile çok turlu konuştu. Backend: GET /api/ai-visibility/personas ve /personas/:key.
 * Yalnız glowy markası + Efe (config/aiVisibilityBrands.js hasPersonas).
 */

const STOP_REASON = {
  got_product: 'Ürün önerdi',
  max_turns: 'Tur sınırı doldu',
  plan_given: 'Teklif ettiği plan kabul edildi',
  no_product: 'Ürün önermedi',
  sim_stop: 'Simülasyon durdu',
  error: 'Hata ile bitti',
};
const STOP_TONE = {
  got_product: 'bg-emerald-50 text-emerald-700', max_turns: 'bg-amber-50 text-amber-700', plan_given: 'bg-blue-50 text-blue-700',
  no_product: 'bg-gray-100 text-gray-600', sim_stop: 'bg-gray-100 text-gray-600', error: 'bg-rose-50 text-rose-700',
};
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const fmtDate = (s) => {
  if (!s) return '–';
  const d = new Date(s);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const num1 = (v) => v.toLocaleString('tr-TR', { maximumFractionDigits: 1, minimumFractionDigits: 1 });
const cap = (s) => (s ? s.charAt(0).toLocaleUpperCase('tr-TR') + s.slice(1) : s);

const apiGet = async (path, brandId) => {
  const res = await fetch(`${API}/api/ai-visibility/${path}`, { credentials: 'include', headers: { 'X-Brand-ID': brandId } });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
  return j;
};

// Dar ekranda taşmayan Seg: seçenekler eşit genişlikte; iconOnlyMobile ile platform adları küçük ekranda gizlenir
function PlatSeg({ value, onChange, options, iconOnlyMobile = false }) {
  return (
    <div className="flex w-full sm:w-auto sm:inline-flex rounded-xl border border-gray-200/80 bg-white p-1 shadow-sm">
      {options.map((o) => (
        <button key={o.value ?? 'all'} onClick={() => onChange(o.value)} title={o.label} aria-label={o.label}
          className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-1.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition min-w-0 ${value === o.value ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
          {o.logo && <span className={`inline-flex rounded shrink-0 ${value === o.value ? 'bg-white p-0.5' : ''}`}><PlatformLogo p={o.logo} size="w-3.5 h-3.5" /></span>}
          <span className={`whitespace-nowrap ${o.logo && iconOnlyMobile ? 'hidden sm:inline' : ''}`}>
            {o.short ? <><span className="sm:hidden">{o.short}</span><span className="hidden sm:inline">{o.label}</span></> : o.label}
          </span>
        </button>
      ))}
    </div>
  );
}


const Pill = ({ children, tone = 'gray', title, className = '' }) => {
  const tones = {
    gray: 'bg-gray-100 text-gray-600', blue: 'bg-blue-50 text-blue-700', amber: 'bg-amber-50 text-amber-700',
    rose: 'bg-rose-50 text-rose-700', green: 'bg-emerald-50 text-emerald-700', purple: 'bg-purple-50 text-purple-700',
  };
  return <span title={title} className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 whitespace-nowrap ${tones[tone]} ${className}`}>{children}</span>;
};

const DrugTag = ({ group }) => (group === 'ilaç'
  ? <span title="Sözlükte ilaç olarak işaretli; reçete durumu doğrulanmadı" className="inline-flex items-center text-[10px] font-medium rounded-full px-1.5 py-px bg-gray-100 text-gray-500 border border-gray-200 whitespace-nowrap">ilaç</span>
  : null);

// ---------- Markdown ----------
const shortLink = (href, text) => {
  const raw = typeof text === 'string' ? text : '';
  if (raw && !/^https?:\/\//i.test(raw)) return raw;
  try { return new URL(href).hostname.replace(/^www\./, ''); } catch { return raw || href; }
};
const MD_COMPONENTS = {
  p: ({ children }) => <p className="my-1.5 first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-1.5 ml-5 list-disc space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 ml-5 list-decimal space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="pl-0.5">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  h1: ({ children }) => <h4 className="text-sm font-bold mt-3 mb-1">{children}</h4>,
  h2: ({ children }) => <h4 className="text-sm font-bold mt-3 mb-1">{children}</h4>,
  h3: ({ children }) => <h4 className="text-sm font-bold mt-3 mb-1">{children}</h4>,
  h4: ({ children }) => <h4 className="text-sm font-bold mt-3 mb-1">{children}</h4>,
  hr: () => <hr className="my-2 border-gray-200" />,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">
      {shortLink(href, React.Children.toArray(children).length === 1 ? React.Children.toArray(children)[0] : '')}
    </a>
  ),
  table: ({ children }) => <div className="overflow-x-auto my-2"><table className="text-xs border-collapse">{children}</table></div>,
  th: ({ children }) => <th className="border border-gray-200 px-2 py-1 bg-gray-50 text-left">{children}</th>,
  td: ({ children }) => <td className="border border-gray-200 px-2 py-1 align-top">{children}</td>,
  code: ({ children }) => <code className="bg-gray-100 rounded px-1 text-[12px]">{children}</code>,
};

function Markdown({ text, collapsible = false }) {
  const long = collapsible && ((text || '').length > 520 || ((text || '').match(/\n/g) || []).length > 8);
  const [open, setOpen] = useState(false);
  const collapsed = long && !open;
  return (
    <div>
      <div className={`relative text-sm leading-relaxed text-gray-800 break-words ${collapsed ? 'max-h-[11.5rem] overflow-hidden' : ''}`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{text || ''}</ReactMarkdown>
        {collapsed && <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none" />}
      </div>
      {long && (
        <button onClick={() => setOpen(!open)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800">
          {open ? <>Daralt <ChevronUp className="w-3.5 h-3.5" /></> : <>Devamını göster <ChevronDown className="w-3.5 h-3.5" /></>}
        </button>
      )}
    </div>
  );
}

// ---------- Konuşma ----------
const MENTION_TONE = {
  top_pick: 'bg-blue-600 text-white', recommended: 'bg-blue-50 text-blue-700 border border-blue-200', mentioned: 'bg-gray-100 text-gray-600',
};
const MENTION_TITLE = { top_pick: 'İlk tercih', recommended: 'Önerildi', mentioned: 'Adı geçti' };

function TurnBadges({ t }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {t.isFollowup && <Pill tone="purple">Sabit takip sorusu</Pill>}
      {t.isPlanAcceptance && <Pill tone="green"><CheckCircle2 className="w-3 h-3" />Teklif kabul edildi</Pill>}
    </div>
  );
}

function AssistantFooter({ t }) {
  const seen = new Set();
  const mentions = (t.mentions || []).filter((m) => (seen.has(m.brand) ? false : seen.add(m.brand)));
  const channels = (t.channels || []);
  const doctor = channels.some((c) => c.type === 'doktor');
  const other = channels.filter((c) => c.type !== 'doktor');
  if (!mentions.length && !channels.length) return null;
  return (
    <div className="mt-2.5 pt-2.5 border-t border-gray-200/70 space-y-1.5">
      {mentions.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1.5">
          {mentions.map((m) => (
            <div key={m.brand} className="min-w-0 max-w-full">
              <span className="inline-flex items-center gap-1">
                <span title={MENTION_TITLE[m.type] || m.type} className={`inline-block text-xs font-semibold rounded-full px-2.5 py-0.5 ${MENTION_TONE[m.type] || MENTION_TONE.mentioned}`}>{m.brand}</span>
                <DrugTag group={m.parentGroup} />
              </span>
              {(m.products || []).length > 0 && <div className="text-[11px] text-gray-500 mt-0.5 px-1 break-words">{m.products.join(', ')}</div>}
            </div>
          ))}
        </div>
      )}
      {(doctor || other.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {doctor && <Pill tone="rose"><Stethoscope className="w-3 h-3" />Dermatoloğa yönlendirdi</Pill>}
          {[...new Set(other.map((c) => c.channel))].map((c) => <Pill key={c} tone="gray">{c}</Pill>)}
        </div>
      )}
    </div>
  );
}

function Sources({ citations }) {
  const [open, setOpen] = useState(false);
  const byDomain = useMemo(() => {
    const m = new Map();
    (citations || []).forEach((c) => { if (!m.has(c.domain)) m.set(c.domain, []); m.get(c.domain).push(c); });
    return [...m.entries()];
  }, [citations]);
  if (!byDomain.length) return null;
  return (
    <div className="mt-3 rounded-xl border border-gray-200/80 bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-gray-600">
        <span>Kaynaklar ({byDomain.length} alan adı)</span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5">
          {byDomain.map(([d, list]) => (
            <a key={d} href={list[0].url} target="_blank" rel="noreferrer" title={list[0].title || d}
              className="flex items-center gap-2 text-xs text-gray-700 hover:text-blue-700">
              <ExternalLink className="w-3 h-3 shrink-0 text-gray-400" />
              <span className="truncate font-medium">{d}</span>
              {list.length > 1 && <span className="text-gray-400 shrink-0">{list.length} sayfa</span>}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function Conversation({ conv, platform, entry }) {
  const ts = conv.turnsDetail || [];
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {conv.runStatus === 'running' && <Pill tone="gray"><Clock className="w-3 h-3" />Ölçüm sürüyor</Pill>}
        {conv.runStatus !== 'running' && conv.extracted === false && <Pill tone="gray"><Clock className="w-3 h-3" />Henüz ayrıştırılmadı</Pill>}
        <Pill tone="gray">{conv.turns} tur</Pill>
        {entry?.firstRecBrand && (
          <Pill tone="gray" title="Konuşmada ilk önerilen marka">
            İlk önerdiği: {entry.firstRecBrand}{entry.firstRecUnmatched && <span className="text-gray-400 font-normal">sözlükte yok</span>}
          </Pill>
        )}
        <Pill tone="gray" title="Model">{conv.model}</Pill>
        {conv.runStatus !== 'running' && conv.extracted !== false && (
        <span className={`inline-flex items-center text-[11px] font-medium rounded-full px-2 py-0.5 ${STOP_TONE[conv.stopReason] || STOP_TONE.sim_stop}`}>
          {STOP_REASON[conv.stopReason] || conv.stopReason}
        </span>
        )}
      </div>
      {conv.rulesVersion && (
        <div className="text-[11px] text-gray-400 -mt-1 mb-3">
          {conv.rulesVersion === 'v2' ? 'Kural v2: 2. turda takip sorusu, teklif kabulü' : 'Kural v1: takip sorusu en geç 4. turda'}
        </div>
      )}
      <div className="space-y-4">
        {ts.map((t) => (
          <div key={t.turnNo} className="space-y-2">
            <div className="flex flex-col items-end gap-1">
              <div className="flex flex-wrap items-center justify-end gap-1.5 max-w-[92%]">
                <span className="text-[11px] text-gray-400">Tur {t.turnNo}</span>
                <TurnBadges t={t} />
              </div>
              <div className="max-w-[92%] rounded-2xl rounded-tr-md bg-blue-600 text-white px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words">
                {t.userText}
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 inline-flex w-7 h-7 rounded-full bg-white border border-gray-200 items-center justify-center shrink-0">
                <PlatformLogo p={platform} size="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1 rounded-2xl rounded-tl-md bg-gray-50 border border-gray-200/70 px-3.5 py-2.5">
                {t.askedQuestion && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 rounded-lg px-2 py-1 mb-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Pill tone="amber"><HelpCircle className="w-3 h-3" />Karşı soru</Pill>
                      {t.optionalQuestion && <Pill tone="gray">İsteğe bağlı</Pill>}
                      <span className="font-semibold">Asistanın sorusu</span>
                    </div>
                    {t.questionSummary && <div className="mt-1">{t.questionSummary}</div>}
                  </div>
                )}
                {t.assistantText ? <Markdown text={t.assistantText} collapsible /> : <span className="text-sm text-gray-400">Yanıt kaydı yok.</span>}
                <AssistantFooter t={t} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <Sources citations={conv.citations} />
    </div>
  );
}

// ---------- Özet matris ----------
// Türkçe tamlama eki: 3'ünde, 4'ünde, 1'inde, 2'sinde...
const suffix = (n) => {
  const last = { 0: 'ında', 1: 'inde', 2: 'sinde', 3: 'ünde', 4: 'ünde', 5: 'inde', 6: 'sında', 7: 'sinde', 8: 'inde', 9: 'unda' }[n % 10];
  return n === 0 ? 'ında' : n === 10 ? 'unda' : last;
};

const entryState = (e) => (e.runStatus === 'running' ? 'running' : e.extracted === false ? 'pending' : 'done');
const hasRec = (e) => !!(e.firstRecBrand || e.firstRecTurn != null || e.recBrandCount > 0 || e.unmatchedRecCount > 0);

function summarize(all) {
  if (!all.length) return null;
  const running = all.filter((e) => entryState(e) === 'running').length;
  const pending = all.filter((e) => entryState(e) === 'pending').length;
  const entries = all.filter((e) => entryState(e) === 'done');
  if (!entries.length) return { state: running ? 'running' : 'pending', running, pending, total: 0 };
  const counts = {};
  const rawCounts = {};
  // Önce sözlükte eşleşen markalar; hiçbiri yoksa ham ad ("sözlükte yok" notuyla)
  entries.forEach((e) => {
    if (!e.firstRecBrand) return;
    const t = e.firstRecUnmatched ? rawCounts : counts;
    t[e.firstRecBrand] = (t[e.firstRecBrand] || 0) + 1;
  });
  const topMatched = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  const top = topMatched || Object.entries(rawCounts).sort((a, b) => b[1] - a[1])[0];
  const unm = top && !topMatched ? { [top[0]]: true } : {};
  const turns = entries.map((e) => e.firstRecTurn).filter((v) => v != null);
  return {
    state: 'done', running, pending,
    brand: top ? top[0] : null,
    brandUnmatched: top ? !!unm[top[0]] : false,
    avgTurn: turns.length ? turns.reduce((a, b) => a + b, 0) / turns.length : null,
    reached: entries.filter(hasRec).length,
    total: entries.length,
    asked: entries.filter((e) => e.askedFirst).length,
    doctor: entries.filter((e) => e.doctorReferral).length,
  };
}

function MatrixCell({ s, onClick, active }) {
  if (!s) return <div className="text-xs text-gray-300 px-2 py-3">Konuşma yok</div>;
  if (s.state !== 'done') {
    return (
      <button onClick={onClick} className={`w-full text-left rounded-xl border px-3 py-2.5 transition hover:border-blue-300 ${active ? 'border-blue-400 bg-blue-50/60' : 'border-gray-200/80 bg-gray-50'}`}>
        <div className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500"><Clock className="w-4 h-4" />{s.state === 'running' ? 'Ölçüm sürüyor' : 'Henüz ayrıştırılmadı'}</div>
      </button>
    );
  }
  return (
    <button onClick={onClick} className={`w-full text-left rounded-xl border px-3 py-2.5 transition hover:border-blue-300 hover:bg-blue-50/40 ${active ? 'border-blue-400 bg-blue-50/60' : 'border-gray-200/80 bg-white'}`}>
      {s.brand ? (
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-400">İlk önerdiği</div>
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-sm font-semibold text-gray-800 truncate">{s.brand}</span>
            {s.brandUnmatched && <span className="text-[10px] text-gray-400 whitespace-nowrap shrink-0">sözlükte yok</span>}
          </div>
        </div>
      ) : (
        <div className="text-sm font-medium text-gray-400">{s.reached > 0 ? 'Sözlükte olmayan marka' : 'Ürün önermedi'}</div>
      )}
      {s.reached > 0 && <div className="text-[11px] text-gray-500 mt-1">
        {s.total} sorunun {s.reached}'{suffix(s.reached)} ürün önerdi{s.avgTurn != null ? (Number.isInteger(s.avgTurn) ? ` · ortalama ${s.avgTurn}. turda` : ` · ortalama ${num1(s.avgTurn)} turda`) : ''}
      </div>}
      {(s.running > 0 || s.pending > 0) && (
        <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-400"><Clock className="w-3 h-3" />
          {[s.running > 0 && `${s.running} soru ölçülüyor`, s.pending > 0 && `${s.pending} soru ayrıştırılmadı`].filter(Boolean).join(', ')}
        </div>
      )}
      <div className="mt-1.5 flex flex-wrap gap-1">
        {s.asked > 0 && <Pill tone="amber" title={`${s.total} sorunun ${s.asked}'${suffix(s.asked)} önce soru sordu`}><HelpCircle className="w-3 h-3" />Önce soru sordu {s.asked}/{s.total}</Pill>}
        {s.doctor > 0 && <Pill tone="rose" title={`${s.total} sorunun ${s.doctor}'${suffix(s.doctor)} dermatoloğa yönlendirdi`}><Stethoscope className="w-3 h-3" />Doktora yönlendirme {s.doctor}/{s.total}</Pill>}
      </div>
    </button>
  );
}

// Yatay kaydırma alanı: sağda içerik kaldıysa solma gölgesi, bittiyse gölge yok
function HScroll({ children, fit = false }) {
  const ref = useRef(null);
  const [more, setMore] = useState(false);
  const check = useCallback(() => {
    const el = ref.current;
    if (el) setMore(el.scrollWidth - el.scrollLeft - el.clientWidth > 4);
  }, []);
  useEffect(() => {
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  });
  return (
    <div className="relative">
      <div ref={ref} onScroll={check} className="overflow-x-auto"><div className={fit ? 'w-full' : 'pr-4 w-full min-w-max'}>{children}</div></div>
      {more && <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent" />}
    </div>
  );
}

// ---------- Isı haritası ----------
const HEAT_STEPS = ['#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb'];
const heatStyle = (v, max) => {
  if (!v) return { background: 'transparent', color: '#d1d5db' };
  const i = Math.min(HEAT_STEPS.length - 1, Math.floor((v / max) * HEAT_STEPS.length));
  return { background: HEAT_STEPS[i], color: i >= 5 ? '#ffffff' : '#1f2937' };
};

// ---------- Sayfa ----------
const Skeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="h-32 rounded-3xl bg-gray-100" />
    <div className="h-72 rounded-3xl bg-gray-100" />
    <div className="h-72 rounded-3xl bg-gray-100" />
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">{Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-40 rounded-2xl bg-gray-100" />)}</div>
  </div>
);


// ---------- Persona görseli / CV ----------
// Avatar dosyaları değişince önbelleği aşmak için tek sürüm sabiti; kırpma konumu da tek yerde
const AVATAR_VERSION = '3d1';
const AVATAR_POSITION = '50% 20%';
const avatarOf = (p) => {
  const base = p?.avatar || (p?.key ? `/persona-${String(p.key).toLowerCase()}.webp` : null);
  return base ? `${base}${base.includes('?') ? '&' : '?'}v=${AVATAR_VERSION}` : null;
};
const cityOf = (c) => String(c || '').split(' (')[0];
// Önce API'nin kısa dert alanı; yoksa mainProblem'in ilk 40 karakteri
const shortProblem = (p) => {
  const given = p?.short_problem || p?.shortProblem || p?.card?.short_problem || p?.card?.shortProblem;
  if (given) return cap(String(given));
  const t = String(p?.mainProblem || '').trim();
  return cap(t.length > 40 ? `${t.slice(0, 40).trim()}...` : t);
};

// Yüz üstte kalsın diye object-position yukarıdan
function PersonaImg({ p, className = '', origin }) {
  const [bad, setBad] = useState(false);
  const src = avatarOf(p);
  if (!src || bad) return <div className={`${className} flex items-center justify-center bg-blue-50 text-blue-700 font-semibold text-2xl`}>{p?.initials}</div>;
  return <img src={src} alt={p.name} loading="lazy" onError={() => setBad(true)} className={className} style={{ objectPosition: AVATAR_POSITION, ...(origin ? { transformOrigin: origin } : {}) }} />;
}
function PersonaAvatar({ p, active, size = 'w-9 h-9' }) {
  return (
    <span className={`${size} rounded-full overflow-hidden shrink-0 inline-flex bg-gray-100 ${active ? 'ring-2 ring-blue-500' : 'ring-1 ring-gray-200'}`}>
      <PersonaImg p={p} className="w-full h-full object-cover scale-[1.6]" origin="50% 28%" />
    </span>
  );
}

const PRINT_CSS = `
@media print {
  @page { size: A4; margin: 8mm; }
  body * { visibility: hidden !important; }
  body *:not(#persona-cv):not(#persona-cv *):not(:has(#persona-cv)) { display: none !important; }
  *:has(#persona-cv) { margin: 0 !important; padding: 0 !important; max-width: none !important; width: auto !important; height: auto !important; min-height: 0 !important; overflow: visible !important; position: static !important; display: block !important; background: #fff !important; }
  #persona-cv, #persona-cv * { visibility: visible !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  #persona-cv { box-shadow: none !important; zoom: 0.72; width: 99% !important; box-sizing: border-box !important; position: relative !important; }
  #persona-cv .cv-hero-grid { display: grid !important; grid-template-columns: 1fr 300px !important; }
  #persona-cv .cv-hero-grid > div:first-child { order: 1 !important; }
  #persona-cv .cv-portrait { order: 2 !important; }
  #persona-cv .cv-story { display: grid !important; grid-template-columns: 1.1fr 1fr !important; }
  #persona-cv .cv-reco { display: grid !important; grid-template-columns: repeat(3, 1fr) !important; }
  #persona-cv .cv-q { break-inside: avoid; }
  #persona-cv .cv-noprint { display: none !important; }
  #persona-cv * { transition: none !important; }
}`;

function summarizePlatform(pl, key, matrix, detail, api) {
  const rows = matrix.filter((m) => m.personaKey === key && m.platform === pl && entryState(m) === 'done');
  const convs = (detail?.conversations || []).filter((c) => c.platform === pl);
  if (api) {
    return {
      brand: api.firstRecBrand, product: api.firstRecProduct, group: api.firstRecParentGroup || null, turn: api.firstRecTurn, doctor: api.doctorReferralCount,
      total: rows.length || convs.length, channels: api.channels || [],
    };
  }
  if (!rows.length && !convs.length) return null;
  const counts = {};
  const rawCounts = {};
  rows.forEach((r) => {
    if (!r.firstRecBrand) return;
    const t = r.firstRecUnmatched ? rawCounts : counts;
    t[r.firstRecBrand] = (t[r.firstRecBrand] || 0) + 1;
  });
  const topMatched = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  const top = topMatched || Object.entries(rawCounts).sort((a, b) => b[1] - a[1])[0];
  const brand = top ? top[0] : null;
  const unmatched = !!top && !topMatched;
  let product = null;
  let group = null;
  if (brand) {
    for (const c of convs) {
      const any = (c.recommendations || []).find((r) => r.brand === brand);
      if (any && !group) group = any.parentGroup || null;
      const rec = (c.recommendations || []).find((r) => r.brand === brand && (r.products || []).length);
      if (rec) { product = rec.products[0]; group = rec.parentGroup || group; break; }
    }
  }
  const turns = rows.map((r) => r.firstRecTurn).filter((v) => v != null);
  const ch = {};
  convs.forEach((c) => (c.channels || []).filter((x) => x.type !== 'doktor').forEach((x) => { ch[x.channel] = (ch[x.channel] || 0) + 1; }));
  return {
    brand, product, group, unmatched, total: rows.length,
    turn: turns.length ? turns.reduce((a, b) => a + b, 0) / turns.length : null,
    doctor: rows.filter((r) => r.doctorReferral).length,
    channels: Object.entries(ch).sort((a, b) => b[1] - a[1]).slice(0, 3).map((x) => x[0]),
    hasDetail: !!detail,
  };
}

// ── Persona kartı (Apple ürün sayfası dili) ─────────────────────────────────
// Üst bant = avatarın düz zemin rengi (card.accent); portre bandın içine oturur, dikiş görünmez.
// Altta her soru × platform önerisi (backend matrix[].topRecs), en altta Glowytics imzası.
const hexRgb = (h) => { const m = String(h || '').match(/^#?([0-9a-f]{6})$/i); if (!m) return null; const n = parseInt(m[1], 16); return [n >> 16, (n >> 8) & 255, n & 255]; };
const isDark = (h) => { const c = hexRgb(h); if (!c) return false; const [r, g, b] = c.map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; }); return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.28; };
const pad2 = (n) => String(n).padStart(2, '0');
// Portre kenarları zemine eriyor (masaüstü: sol + üst; avatar zemini ortada açık, köşelerde koyu)
const CV_FADE_CSS = `
@media (min-width: 768px) {
  #persona-cv .cv-fade {
    -webkit-mask-image: linear-gradient(to right, transparent, #000 26%), linear-gradient(to bottom, transparent, #000 14%);
    -webkit-mask-composite: source-in;
    mask-image: linear-gradient(to right, transparent, #000 26%), linear-gradient(to bottom, transparent, #000 14%);
    mask-composite: intersect;
  }
}`;

function OpenerRecs({ rows, platforms }) {
  if (!rows.length) return null;
  return (
    <div className="cv-reco mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
      {platforms.map((pl) => {
        const r = rows.find((x) => x.platform === pl);
        const recs = r?.topRecs || [];
        const [first, ...rest] = recs;
        return (
          <div key={pl} className="rounded-2xl bg-[#f5f5f7] px-4 py-3.5 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-[#6e6e73]"><PlatformLogo p={pl} size="w-3.5 h-3.5" />{PLATFORM_LABEL[pl]}</div>
            {!r ? <div className="mt-2 text-[13px] text-[#86868b]">Sorulmadı</div>
              : !r.extracted ? <div className="mt-2 text-[13px] text-[#86868b]">Henüz ayrıştırılmadı</div>
              : !first ? <div className="mt-2 text-[13px] text-[#86868b]">Marka önermedi</div>
              : (
                <>
                  <div className="mt-2.5 min-h-[26px] flex items-center">
                    <AiBrandLogo name={first.brand} size={26} maxW={160}
                      fallback={<span className="text-[17px] font-semibold tracking-[-0.01em] text-[#1d1d1f] leading-snug">{first.brand}</span>} />
                  </div>
                  {first.product && <div className="text-[13px] leading-snug text-[#424245] break-words">{first.product}</div>}
                  {rest.length > 0 && (
                    <div className="mt-2.5 pt-2.5 border-t border-black/[0.06] space-y-1">
                      {rest.map((x) => (
                        <div key={x.brand} className="text-[12px] leading-snug text-[#6e6e73] break-words">
                          <span className="font-medium text-[#424245]">{x.brand}</span>{x.product ? ` · ${x.product}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
          </div>
        );
      })}
    </div>
  );
}

function SpecCell({ label, children, dark }) {
  if (children == null || children === '' || (Array.isArray(children) && !children.length)) return null;
  return (
    <div className={`rounded-2xl px-4 py-3 backdrop-blur-md ${dark ? 'bg-white/10 ring-1 ring-white/15' : 'bg-white/55 ring-1 ring-black/5'}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-[0.08em] ${dark ? 'text-white/60' : 'text-black/45'}`}>{label}</div>
      <div className={`mt-1 text-[14px] leading-snug ${dark ? 'text-white' : 'text-[#1d1d1f]'}`}>{children}</div>
    </div>
  );
}

function StoryField({ label, children }) {
  if (children == null || children === '') return null;
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">{label}</dt>
      <dd className="mt-1.5 text-[15px] leading-relaxed text-[#1d1d1f]">{children}</dd>
    </div>
  );
}

function PersonaCV({ persona: ps, matrix, detail, platforms }) {
  // "Bilmiyor" yalnız skinTypeKnown===false iken; alan yoksa satır gizli
  const skin = ps.skinTypeKnown === false ? 'Bilmiyor' : (ps.skinType || null);
  const apiSum = ps.summaryByPlatform || null;
  const accent = ps.accent || '#e8e8ed';
  const dark = isDark(accent);
  const ink = dark ? 'text-white' : 'text-[#1d1d1f]';
  const sub = dark ? 'text-white/70' : 'text-black/55';
  const openers = ps.openers || [];
  return (
    <div id="persona-cv" className="relative bg-white rounded-[32px] overflow-hidden ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_24px_64px_-24px_rgba(0,0,0,0.22)]">
      <style>{PRINT_CSS + CV_FADE_CSS}</style>

      {/* Üst bant: avatar zemini rengi, portre sağda bandın içine oturur */}
      <div className="cv-hero relative" style={{ background: accent }}>
        <div className="cv-hero-grid grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(260px,400px)] items-end">
          <div className="relative z-10 px-6 sm:px-12 pt-8 sm:pt-12 pb-8 sm:pb-12 order-2 md:order-1">
            <div className={`text-[12px] font-semibold uppercase tracking-[0.12em] ${sub}`}>Persona {ps.key}{ps.shortProblem ? ` · ${shortProblem(ps)}` : ''}</div>
            <h2 className={`mt-3 text-[40px] sm:text-[56px] leading-[1.02] font-semibold tracking-[-0.035em] ${ink}`}>{ps.name}</h2>
            <p className={`mt-3 text-[17px] sm:text-[19px] leading-snug ${sub}`}>{ps.age} yaş · {cityOf(ps.city)} · {ps.job}</p>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-2.5 max-w-3xl">
              <SpecCell label="Bütçe" dark={dark}>{ps.budget}</SpecCell>
              <SpecCell label="Nereden alır" dark={dark}>{(ps.channels || []).join(' · ')}</SpecCell>
              <SpecCell label="Cilt / saç tipi" dark={dark}>{skin}</SpecCell>
            </div>
          </div>
          <div className="cv-portrait relative order-1 md:order-2 self-end">
            <div className="cv-fade aspect-square w-full max-w-[340px] md:max-w-[400px] mx-auto md:mr-0">
              <PersonaImg p={ps} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
        <button onClick={() => window.print()}
          className={`cv-noprint absolute top-4 right-4 z-20 inline-flex items-center gap-1.5 text-[12px] font-medium rounded-full px-3 py-1.5 backdrop-blur-md transition ${dark ? 'bg-white/15 text-white hover:bg-white/25' : 'bg-white/60 text-[#1d1d1f] hover:bg-white/80'}`}>
          <Printer className="w-3.5 h-3.5" />PDF
        </button>
      </div>

      <div className="px-6 sm:px-12 py-10 sm:py-14 space-y-14">
        {/* Hikâye */}
        <section className="cv-story grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-8 lg:gap-14">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">Derdi</div>
            <p className="mt-2 text-[24px] sm:text-[28px] leading-[1.25] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{cap(ps.mainProblem)}</p>
            {ps.secondary && <p className="mt-4 text-[17px] leading-relaxed text-[#424245]">{cap(ps.secondary)}</p>}
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 content-start">
            <StoryField label="Daha önce denedikleri">{cap(ps.tried)}</StoryField>
            <StoryField label="Hassasiyetler">{cap(ps.sensitivities)}</StoryField>
            <div className="sm:col-span-2"><StoryField label="Nasıl yazıyor">{cap(ps.style)}</StoryField></div>
          </dl>
        </section>

        {ps.highlight && (
          <section className="rounded-[28px] px-7 sm:px-10 py-8" style={{ background: `${accent}1f` }}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">Çarpıcı not</div>
            <p className="mt-2 text-[22px] sm:text-[26px] leading-[1.3] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{ps.highlight}</p>
          </section>
        )}

        {/* Sorular × öneriler */}
        <section>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">Yapay zekaya sordukları</div>
              <h3 className="mt-1.5 text-[28px] sm:text-[32px] leading-tight font-semibold tracking-[-0.025em] text-[#1d1d1f]">{openers.length} soru, {platforms.length} yapay zeka.</h3>
            </div>
            <p className="text-[13px] text-[#86868b] max-w-sm">Her kutuda o platformun önerdiği ilk marka ve ürün; altta sıradaki öneriler.</p>
          </div>
          <div className="mt-8 divide-y divide-black/[0.06]">
            {openers.map((o, i) => (
              <div key={o.promptKey} className="cv-q py-8 first:pt-0 last:pb-0">
                <div className="flex gap-5 sm:gap-8">
                  <div className="shrink-0 w-10 sm:w-14 text-[28px] sm:text-[40px] leading-none font-semibold tracking-[-0.04em] text-[#d2d2d7] tabular-nums">{pad2(o.openerNo || i + 1)}</div>
                  <div className="min-w-0 flex-1">
                    {o.category && <div className="text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: dark ? accent : '#6e6e73' }}>{catLabel(o.category)}</div>}
                    <p className="mt-1 text-[19px] sm:text-[21px] leading-[1.4] font-medium tracking-[-0.01em] text-[#1d1d1f] break-words">&ldquo;{o.text}&rdquo;</p>
                    <OpenerRecs rows={matrix.filter((m) => m.personaKey === ps.key && m.promptKey === o.promptKey)} platforms={platforms} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Platform özeti */}
        <section>
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">Genel tablo</div>
          <h3 className="mt-1.5 text-[24px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">Platform platform ilk tercih.</h3>
          <div className="cv-reco mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            {platforms.map((pl) => {
              const sm = summarizePlatform(pl, ps.key, matrix, detail, apiSum?.[pl]);
              return (
                <div key={pl} className="rounded-[22px] ring-1 ring-black/[0.06] bg-white p-5 min-w-0">
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1d1d1f]"><PlatformLogo p={pl} size="w-5 h-5" />{PLATFORM_LABEL[pl]}</div>
                  {!sm ? <div className="mt-3 text-sm text-[#86868b]">Bu platformda konuşma yok</div> : (
                    <div className="mt-4 space-y-2.5">
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">En sık ilk önerisi</div>
                        <div className="mt-1.5 min-h-[30px] flex items-center gap-1.5 flex-wrap">{sm.brand ? <AiBrandLogo name={sm.brand} size={26} maxW={170} fallback={<span className="text-[22px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{sm.brand}</span>} /> : <span className="text-[22px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">Ürün önermedi</span>}<DrugTag group={sm.group} />{sm.unmatched && <span className="text-[11px] text-[#86868b]">sözlükte yok</span>}</div>
                        {sm.product && <div className="text-[13px] text-[#424245] mt-0.5 break-words">{sm.product}</div>}
                      </div>
                      {sm.turn != null && <div className="text-[12px] text-[#6e6e73]">Ortalama {Number.isInteger(sm.turn) ? `${sm.turn}. turda` : `${num1(sm.turn)} turda`} ürüne ulaştı</div>}
                      {sm.doctor > 0 && <div className="text-[12px] text-[#6e6e73]">Doktora yönlendirme: {sm.total ? `${sm.total} sorunun ${sm.doctor}'${suffix(sm.doctor)}` : sm.doctor}</div>}
                      {sm.channels?.length > 0 && <div className="flex flex-wrap gap-1">{sm.channels.map((c) => <Pill key={c} tone="gray">{c}</Pill>)}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* İmza */}
      <div className="cv-foot flex items-center justify-between gap-4 px-6 sm:px-12 py-5 border-t border-black/[0.06] bg-[#fbfbfd]">
        <div className="text-[12px] text-[#86868b]">AI Visibility · Persona çalışması</div>
        <img src="/glowytics-lockup-blue.png" alt="Glowytics" className="h-6 sm:h-7 w-auto select-none" draggable={false} />
      </div>
    </div>
  );
}

export default function AiVisibilityPersonas() {
  const { selectedBrand, user } = useAuth();
  const brandId = selectedBrand?.id;
  const allowed = hasPersonas(brandId, user?.email);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [heatPlatform, setHeatPlatform] = useState(null);
  const [selKey, setSelKey] = useState(null);
  const [openerKey, setOpenerKey] = useState(null);
  const [mobilePlat, setMobilePlat] = useState(null);
  const [matPlat, setMatPlat] = useState(null);
  const [tab, setTab] = useState('personalar');
  const [details, setDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const detailRef = useRef(null);

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true); setError(null);
    try {
      const j = await apiGet('personas', brandId);
      setData(Array.isArray(j.personas) ? j : (j.personas || j));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [allowed, brandId]);
  useEffect(() => { load(); }, [load]);

  const personas = useMemo(() => data?.personas || [], [data]);
  const matrix = useMemo(() => data?.matrix || [], [data]);
  const platforms = useMemo(() => {
    const set = new Set([...(data?.platforms || []), ...matrix.map((m) => m.platform)]);
    return PLATFORM_ORDER.filter((p) => set.has(p));
  }, [data, matrix]);

  useEffect(() => {
    if (personas.length && !selKey) setSelKey(personas[0].key);
  }, [personas, selKey]);
  useEffect(() => { if (platforms.length && !mobilePlat) setMobilePlat(platforms[0]); }, [platforms, mobilePlat]);

  const selPersona = personas.find((p) => p.key === selKey) || null;
  useEffect(() => {
    if (selPersona && !selPersona.openers?.some((o) => o.promptKey === openerKey)) setOpenerKey(selPersona.openers?.[0]?.promptKey || null);
  }, [selPersona, openerKey]);

  useEffect(() => {
    if (!allowed || !selKey || details[selKey]) return undefined;
    let alive = true;
    setDetailLoading(true); setDetailError(null);
    apiGet(`personas/${selKey}`, brandId)
      .then((j) => { if (alive) setDetails((d) => ({ ...d, [selKey]: j })); })
      .catch((e) => { if (alive) setDetailError(e.message); })
      .finally(() => { if (alive) setDetailLoading(false); });
    return () => { alive = false; };
  }, [allowed, selKey, brandId, details]);

  const pick = (key, platform, opener) => {
    setSelKey(key);
    if (platform) setMobilePlat(platform);
    if (opener) setOpenerKey(opener);
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };

  if (!allowed) {
    return (
      <div className="p-6 max-w-3xl mx-auto" lang="tr">
        <Card foot={false} icon={Info} tone="gray" title="Bu sayfa bu marka için yok" sub="Persona konuşmaları yalnızca derma ve kozmetik sektörü AI Visibility projelerinde bulunur.">
          <Link to="/ai-visibility" className="text-sm font-medium text-blue-600 hover:text-blue-800">AI Visibility özetine git</Link>
        </Card>
      </div>
    );
  }

  const heatRows = (() => {
    if (!data?.heatmap) return [];
    return heatPlatform ? (data.heatmap.platformSplit?.[heatPlatform]?.rows || []) : (data.heatmap.rows || []);
  })();
  const heatBrands = (() => {
    const all = data?.heatmap?.brands || [];
    const tot = (b) => heatRows.reduce((a, r) => a + (r.counts?.[b] || 0), 0);
    return all.map((b) => ({ b, t: tot(b) })).filter((x) => x.t > 0).sort((a, b) => b.t - a.t).map((x) => x.b);
  })();
  const heatMax = Math.max(1, ...heatRows.flatMap((r) => Object.values(r.counts || {})));
  const convTotal = matrix.length;
  const pendingCount = Math.max(data?.pendingConversations || 0, matrix.filter((m) => entryState(m) !== 'done').length);
  const names = platforms.map((p) => PLATFORM_LABEL[p]);
  const platformText = names.length === 0 ? 'yapay zeka asistanları' : names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} ve ${names[names.length - 1]}`;

  const detail = details[selKey];
  const convs = (detail?.conversations || []).filter((c) => c.promptKey === openerKey);


  return (
    <div className="min-h-screen" lang="tr">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">AI Visibility · Personalar</h1>
          {tab === 'personalar' && <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            {data?.project?.slug === 'kozmetik-tr' ? 'Makyaj, bakım, parfüm ve saç ihtiyacı olan' : 'Cilt derdi olan'} {personas.length || 10} kurgusal kullanıcı, kendi dilleriyle yazdıkları açılış sorusundan başlayarak {platformText} ile çok turlu konuştu.
            Önce özet tabloya bak, sonra bir persona seç ve konuşmayı tur tur oku: yapay zeka neyi sordu, hangi markayı ve ürünü kaçıncı turda önerdi.
          </p>}
        </div>

        <PlatSeg value={tab} onChange={setTab} options={[
          { value: 'personalar', label: 'Personalar', short: 'Personalar' }, { value: 'fiyat', label: 'Fiyat bantları', short: 'Fiyat' }, { value: 'alt', label: 'Alternatifler', short: 'Alternatif' }, { value: 'urun', label: 'Ürünler', short: 'Ürünler' },
        ]} />

        {tab === 'fiyat' && <PriceBandsTab brandId={brandId} />}
        {tab === 'alt' && <AlternativesTab brandId={brandId} />}
        {tab === 'urun' && <ProductsTab brandId={brandId} />}

        {tab === 'personalar' && loading && !data && <Skeleton />}

        {tab === 'personalar' && error && !data && (
          <Card foot={false} icon={AlertTriangle} tone="rose" title="Veri yüklenemedi" sub={error}>
            <button onClick={load} className="text-sm font-medium text-blue-600 hover:text-blue-800">Tekrar dene</button>
          </Card>
        )}

        {tab === 'personalar' && data && personas.length === 0 && (
          <Card foot={false} icon={Users} tone="gray" title="Persona verisi yok" sub="Bu proje için henüz persona konuşması kaydedilmemiş." />
        )}

        {tab === 'personalar' && data && personas.length > 0 && (
          <>
            {/* Künye */}
            <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm p-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div><div className="text-xs text-gray-500">Konuşma</div><div className="text-2xl font-semibold text-gray-900">{convTotal}</div>
                {pendingCount > 0 && <div className="text-[11px] text-gray-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{pendingCount} ayrıştırılmayı bekliyor</div>}</div>
              <div><div className="text-xs text-gray-500">Persona</div><div className="text-2xl font-semibold text-gray-900">{personas.length}</div></div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Platformlar</div>
                <div className="flex flex-wrap gap-2">{platforms.map((p) => (
                  <span key={p} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-800"><PlatformLogo p={p} />{PLATFORM_LABEL[p]}</span>
                ))}</div>
              </div>
              <div><div className="text-xs text-gray-500">Son güncelleme</div><div className="text-sm font-medium text-gray-800 mt-1">{fmtDate(data.updatedAt)}</div></div>
            </div>

            {/* Persona kartları */}
            <div>
              <div className="flex items-end justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Personalar</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Bir kişiyi seç, CV'sini ve yapay zekayla konuşmalarını gör.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                {personas.map((ps) => {
                  const active = ps.key === selKey;
                  return (
                    <button key={ps.key} onClick={() => pick(ps.key)}
                      className={`group text-left rounded-3xl bg-white border shadow-sm overflow-hidden transition duration-200 hover:-translate-y-1 hover:shadow-lg min-w-0 ${active ? 'border-blue-500 ring-4 ring-blue-200' : 'border-gray-200/80'}`}>
                      <div className="aspect-square bg-gray-100 overflow-hidden">
                        <PersonaImg p={ps} className="w-full h-full object-cover transition duration-300 group-hover:scale-105" />
                      </div>
                      <div className="p-3 sm:p-4">
                        <div className="text-sm sm:text-base font-semibold text-gray-900 truncate">{ps.name}</div>
                        <div className="text-xs text-gray-500 truncate">{ps.age} yaş · {cityOf(ps.city)}</div>
                        <div className="text-xs text-blue-700 mt-1.5 truncate" title={ps.mainProblem}>{shortProblem(ps)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Seçili persona detayı */}
            <div ref={detailRef} className="scroll-mt-4 space-y-6">
              {selPersona && <PersonaCV persona={selPersona} matrix={matrix} detail={detail} platforms={platforms} />}
              {selPersona && (
                <Card icon={MessageSquare} tone="green" title={`${selPersona.name} · konuşmalar`}
                  sub="Açılış sorusunu seç, her platformda konuşmayı tur tur oku.">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    Açılış sorusu <span className="normal-case font-normal text-gray-400 ml-1">yeşil nokta: ürün önerdi, gri: önermedi, saat: ölçüm bekliyor</span>
                  </div>
                  <div className="flex flex-col gap-2 mb-5">
                    {(selPersona.openers || []).map((o) => (
                      <button key={o.promptKey} onClick={() => setOpenerKey(o.promptKey)}
                        className={`text-left rounded-xl border px-3.5 py-2.5 text-sm transition ${o.promptKey === openerKey ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-gray-200/80 bg-white text-gray-700 hover:border-blue-300'}`}>
                        <span className="flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-3">
                          <span className="min-w-0 flex-1">
                            <span className="font-semibold mr-2">Soru {o.openerNo}</span>{o.text}
                            {platforms.map((pl) => {
                              const e = matrix.find((m) => m.promptKey === o.promptKey && m.platform === pl);
                              return e?.firstRecUnmatched && e.firstRecBrand
                                ? <span key={pl} className="block text-[11px] text-gray-400 mt-0.5">{PLATFORM_LABEL[pl]}: ilk önerdiği {e.firstRecBrand}, sözlükte yok</span> : null;
                            })}
                          </span>
                          <span className="flex items-center gap-2 shrink-0 pt-0.5">
                            {platforms.map((pl) => {
                              const e = matrix.find((m) => m.promptKey === o.promptKey && m.platform === pl);
                              const st = e ? entryState(e) : null;
                              const got = e && st === 'done' && hasRec(e);
                              const wait = st === 'running' || st === 'pending';
                              return (
                                <span key={pl} className="inline-flex items-center gap-1" title={`${PLATFORM_LABEL[pl]}: ${!e ? 'konuşma yok' : st === 'running' ? 'ölçüm sürüyor' : st === 'pending' ? 'henüz ayrıştırılmadı' : got ? 'ürün önerdi' : 'ürün önermedi'}`}>
                                  <PlatformLogo p={pl} size="w-3.5 h-3.5" />
                                  {wait ? <Clock className="w-2.5 h-2.5 text-gray-400" /> : <span className={`w-2 h-2 rounded-full ${got ? 'bg-emerald-500' : e ? 'bg-gray-300' : 'border border-gray-300'}`} />}
                                </span>
                              );
                            })}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="lg:hidden mb-4">
                    <PlatSeg value={mobilePlat} onChange={setMobilePlat}
                      options={platforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p], logo: p }))} />
                  </div>

                  {detailLoading && !detail && (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center"><Loader2 className="animate-spin" size={16} />Konuşmalar yükleniyor</div>
                  )}
                  {detailError && !detail && <div className="text-sm text-rose-600 py-6">Konuşmalar yüklenemedi: {detailError}</div>}
                  {detail && !(detail.conversations || []).length && (
                    <div className="text-sm text-gray-400 py-8 text-center">Bu persona için konuşma verisi yok.</div>
                  )}
                  {detail && (detail.conversations || []).length > 0 && (
                    <div className={`grid gap-6 ${platforms.length >= 3 ? 'lg:grid-cols-3' : platforms.length === 2 ? 'lg:grid-cols-2' : ''}`}>
                      {platforms.map((p) => {
                        const list = convs.filter((c) => c.platform === p);
                        return (
                          <div key={p} className={`min-w-0 ${p === mobilePlat ? 'block' : 'hidden'} lg:block`}>
                            <div className="hidden lg:flex items-center gap-2 mb-3 pb-2 border-b border-gray-100 text-sm font-semibold text-gray-800">
                              <PlatformLogo p={p} size="w-5 h-5" />{PLATFORM_LABEL[p]}
                            </div>
                            {list.length === 0
                              ? <div className="text-sm text-gray-400 rounded-xl border border-dashed border-gray-200 py-8 text-center">Bu platformda konuşma yok</div>
                              : list.map((c) => <Conversation key={c.responseId} conv={c} platform={p} entry={matrix.find((m) => m.responseId === c.responseId)} />)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              )}
            </div>
            {/* Özet matris */}
            <Card icon={Grid3x3} title="Özet: kime, hangi platformda ne önerildi"
              sub="Hücre, personanın açılış sorularının ortalamasıdır. Bir hücreye tıklayınca konuşma aşağıda açılır.">
              <div className="md:hidden mb-3">
                <PlatSeg value={matPlat || platforms[0]} onChange={setMatPlat}
                  options={platforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p], logo: p }))} />
              </div>
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full table-fixed border-separate border-spacing-y-2">
                  <thead>
                    <tr>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 w-14 sm:w-40 md:w-44"><span className="hidden sm:inline">Persona</span></th>
                      {platforms.map((p) => (
                        <th key={p} className={`${p === (matPlat || platforms[0]) ? '' : 'hidden'} md:table-cell text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-1`}>
                          <span className="inline-flex items-center gap-1.5"><PlatformLogo p={p} />{PLATFORM_LABEL[p]}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {personas.map((ps) => (
                      <tr key={ps.key}>
                        <td className="px-2 align-middle">
                          <button onClick={() => pick(ps.key)} className="flex items-center gap-2 text-left">
                            <span className="flex flex-col items-center sm:contents">
                              <PersonaAvatar p={ps} active={ps.key === selKey} size="w-9 h-9" />
                              <span className="sm:hidden text-[10px] text-gray-600 mt-0.5 leading-none">{ps.name.split(' ')[0]}</span>
                            </span>
                            <span className="min-w-0 hidden sm:block"><span className="block text-sm font-medium text-gray-800 truncate">{ps.name}</span><span className="block text-[11px] text-gray-500 truncate max-w-[150px]">{ps.age} yaş · {shortProblem(ps)}</span></span>
                          </button>
                        </td>
                        {platforms.map((pl) => {
                          const s = summarize(matrix.filter((m) => m.personaKey === ps.key && m.platform === pl));
                          return (
                            <td key={pl} className={`${pl === (matPlat || platforms[0]) ? '' : 'hidden'} md:table-cell px-1 align-top`}>
                              <MatrixCell s={s} active={ps.key === selKey && mobilePlat === pl} onClick={() => pick(ps.key, pl)} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                Konuşma kuralları: ChatGPT açılış 1 ve Claude v2 (2. turda takip sorusu, teklif kabulü), diğerleri v1 (takip sorusu en geç 4. turda).
              </p>
            </Card>

            {/* Isı haritası */}
            <Card icon={Flame} tone="rose" title="En çok önerilen markalar, persona bazında"
              sub="Hücredeki sayı, markanın o personanın konuşmalarında önerilme sayısıdır."
              right={<div className="hidden sm:block"><PlatSeg value={heatPlatform} onChange={setHeatPlatform}
                iconOnlyMobile options={[{ value: null, label: 'Tümü' }, ...platforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p], logo: p }))]} /></div>}>
              <div className="sm:hidden mb-3"><PlatSeg value={heatPlatform} onChange={setHeatPlatform}
                iconOnlyMobile options={[{ value: null, label: 'Tümü' }, ...platforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p], logo: p }))]} /></div>
              {pendingCount > 0 && <div className="text-[11px] text-gray-400 mb-2 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{pendingCount} konuşma henüz ayrıştırılmadı, sayıya katılmadı.</div>}
              {heatBrands.length === 0 ? (
                <div className="text-sm text-gray-400 py-6 text-center">Bu platformda önerilen marka yok.</div>
              ) : (
                <HScroll>
                  <table className="border-separate border-spacing-0 text-sm">
                    <thead>
                      <tr>
                        <th className="sticky left-0 z-10 bg-white text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-3 py-2 min-w-[150px]">Persona</th>
                        {heatBrands.map((b) => (
                          <th key={b} className="text-[11px] font-semibold text-gray-600 px-0 py-2 text-center align-bottom w-[66px] min-w-[66px] leading-tight">{b}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {heatRows.map((r) => {
                        const ps = personas.find((p) => p.key === r.personaKey);
                        return (
                          <tr key={r.personaKey}>
                            <td className="sticky left-0 z-10 bg-white px-3 py-1 border-t border-gray-100 whitespace-nowrap">
                              <button onClick={() => pick(r.personaKey)} className="text-sm font-medium text-gray-800 hover:text-blue-700">
                                <span className="inline-flex items-center gap-2">{ps && <PersonaAvatar p={ps} size="w-6 h-6" />}{ps?.name || r.personaKey}</span>
                              </button>
                            </td>
                            {heatBrands.map((b) => {
                              const v = r.counts?.[b] || 0;
                              return (
                                <td key={b} className="border-t border-gray-100 p-0.5">
                                  <div className="h-9 rounded-lg flex items-center justify-center text-sm font-semibold" style={heatStyle(v, heatMax)}>{v || ''}</div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </HScroll>
              )}
            </Card>

          </>
        )}
      </div>
    </div>
  );
}

// ---------- Ortak: yükleme / hata ----------
function useApi(path, brandId) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    setState((x) => ({ ...x, loading: true, error: null }));
    apiGet(path, brandId)
      .then((j) => alive && setState({ data: j, loading: false, error: null }))
      .catch((e) => alive && setState({ data: null, loading: false, error: e.message }));
    return () => { alive = false; };
  }, [path, brandId, n]);
  return { ...state, retry: () => setN((v) => v + 1) };
}

const TabSkeleton = () => (
  <div className="space-y-4 animate-pulse"><div className="h-14 rounded-3xl bg-gray-100" /><div className="h-80 rounded-3xl bg-gray-100" /></div>
);
const TabError = ({ error, retry }) => (
  <Card foot={false} icon={AlertTriangle} tone="rose" title="Veri yüklenemedi" sub={error}>
    <button onClick={retry} className="text-sm font-medium text-blue-600 hover:text-blue-800">Tekrar dene</button>
  </Card>
);

// ---------- Fiyat bantları ----------
const CATEGORY_TR = {
  akne: 'Akne', leke: 'Leke', hassas: 'Hassas cilt', kuru: 'Kuru cilt', gunes_yuz: 'Yüz güneş kremi', temizleyici: 'Temizleyici',
  nemlendirici: 'Nemlendirici', serum: 'Serum', yaslanma: 'Yaşlanma karşıtı', bebek_cocuk: 'Bebek/çocuk', atopik: 'Atopik cilt', sac_dokulme: 'Saç dökülmesi',
};
const CATEGORY_ORDER = Object.keys(CATEGORY_TR);
const tl = (n) => Number(n).toLocaleString('tr-TR');
const parseBand = (b) => {
  const m = String(b).match(/^(\d+)-(\d+)$/);
  if (m) return { lo: +m[1], hi: +m[2] };
  const m2 = String(b).match(/^(\d+)\+$/);
  return m2 ? { lo: +m2[1], hi: Infinity } : { lo: 0, hi: Infinity };
};
const bandLabel = (b) => {
  const { lo, hi } = parseBand(b);
  if (lo === 0 && hi !== Infinity) return `${tl(hi)} TL altı`;
  if (hi === Infinity) return `${tl(lo)} TL üstü`;
  return `${tl(lo)}-${tl(hi)} TL`;
};
// "yaklaşık 486.96 TL", "230–300 TL", "1.200 TL" gibi metinden sayıları çıkarır
const parsePrices = (txt) => {
  if (!txt) return [];
  return (String(txt).match(/\d[\d.,]*/g) || []).map((t) => {
    t = t.replace(/[.,]$/, '');
    if (/^\d{1,3}(\.\d{3})+$/.test(t)) return Number(t.replace(/\./g, ''));
    if (/^\d{1,3}(,\d{3})+$/.test(t)) return Number(t.replace(/,/g, ''));
    return Number(t.replace(',', '.'));
  }).filter((v) => Number.isFinite(v) && v >= 20);
};
const outOfBand = (txt, band) => {
  const v = parsePrices(txt);
  if (!v.length) return false;
  const { lo, hi } = parseBand(band);
  return Math.min(...v) > hi * 1.05 || Math.max(...v) < lo * 0.95;
};

function PriceCell({ row, band }) {
  if (!row) return <div className="text-xs text-gray-300 py-3">Cevap yok</div>;
  return (
    <div className="rounded-xl border border-gray-200/80 bg-white p-3 h-full">
      <div className="text-[11px] text-gray-400 leading-snug mb-2">{row.text}</div>
      {row.brands.length === 0 && <div className="text-xs text-gray-400">Marka önermedi</div>}
      <div className="space-y-1.5">
        {row.brands.map((b) => {
          const out = outOfBand(b.priceMentioned, band);
          return (
            <div key={b.brand} className="min-w-0">
              <div className="flex flex-wrap items-center gap-1">
                <span className={`inline-block text-xs font-semibold rounded-full px-2.5 py-0.5 ${MENTION_TONE[b.type] || MENTION_TONE.mentioned}`}>{b.brand}</span>
                <DrugTag group={b.parentGroup} />
                {out && <Pill tone="amber" title="Söylenen fiyat sorulan bandın dışında">bant dışı</Pill>}
              </div>
              {(b.products || []).length > 0 && <div className="text-[11px] text-gray-500 mt-0.5 px-1 break-words">{b.products.join(', ')}</div>}
              {b.priceMentioned && <div className="text-[11px] text-gray-600 px-1">{b.priceMentioned}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PriceBandsTab({ brandId }) {
  const { data, loading, error, retry } = useApi('price-bands', brandId);
  const [cat, setCat] = useState(null);
  const [plat, setPlat] = useState(null);
  const rows = useMemo(() => data?.rows || [], [data]);
  const cats = useMemo(() => CATEGORY_ORDER.filter((c) => rows.some((r) => r.category === c)), [rows]);
  const platforms = useMemo(() => PLATFORM_ORDER.filter((p) => rows.some((r) => r.platform === p)), [rows]);
  const sel = cat || cats[0];
  const bands = useMemo(() => [...new Set(rows.filter((r) => r.category === sel).map((r) => r.priceBand))].sort((a, b) => parseBand(a).lo - parseBand(b).lo), [rows, sel]);
  const mobPlat = plat || platforms[0];

  if (loading && !data) return <TabSkeleton />;
  if (error) return <TabError error={error} retry={retry} />;
  if (!rows.length) return <Card foot={false} icon={Info} tone="gray" title="Fiyat bandı verisi yok" sub="Bu proje için fiyat bandı soruları henüz çalıştırılmamış." />;

  return (
    <Card icon={Tags} tone="amber" title="Fiyat bandı sorulduğunda ne önerildi"
      sub="Aynı ürün kategorisi, üç farklı bütçe cümlesi. Her hücre tek cevaptır; yön gösterir.">
      <div className="flex flex-wrap gap-2 mb-5">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)}
            className={`text-sm font-medium px-3 py-1.5 rounded-xl border shadow-sm ${c === sel ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200/80 text-gray-700 hover:bg-gray-50'}`}>
            {CATEGORY_TR[c]}
          </button>
        ))}
      </div>
      <div className="md:hidden mb-4">
        <PlatSeg value={mobPlat} onChange={setPlat} options={platforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p], logo: p }))} />
      </div>
      <div className="space-y-4">
        <div className="hidden md:grid gap-4 text-xs font-semibold text-gray-600 uppercase tracking-wider" style={{ gridTemplateColumns: `110px repeat(${platforms.length}, minmax(0, 1fr))` }}>
          <div>Bütçe bandı</div>
          {platforms.map((p) => <div key={p} className="inline-flex items-center gap-1.5"><PlatformLogo p={p} />{PLATFORM_LABEL[p]}</div>)}
        </div>
        {bands.map((b) => (
          <div key={b} className="grid gap-3 md:gap-4 md:grid-cols-[110px_1fr] items-start">
            <div className="text-sm font-semibold text-gray-800 pt-1 md:pt-3">{bandLabel(b)}</div>
            <div className="min-w-0">
              <div className="hidden md:grid gap-4 items-stretch" style={{ gridTemplateColumns: `repeat(${platforms.length}, minmax(0, 1fr))` }}>
                {platforms.map((p) => <PriceCell key={p} band={b} row={rows.find((r) => r.category === sel && r.priceBand === b && r.platform === p)} />)}
              </div>
              <div className="md:hidden">
                <PriceCell band={b} row={rows.find((r) => r.category === sel && r.priceBand === b && r.platform === mobPlat)} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------- Alternatifler ----------
function AlternativesTab({ brandId }) {
  const { data, loading, error, retry } = useApi('alternatives', brandId);
  const [target, setTarget] = useState(null);
  const [plat, setPlat] = useState(null);
  const [query, setQuery] = useState('');
  const [showAllRows, setShowAllRows] = useState(false);
  const matrix = useMemo(() => data?.matrix || [], [data]);
  // En çok alternatif bahsi alan hedefler başta
  const targets = useMemo(() => {
    const tot = {};
    matrix.forEach((m) => { tot[m.target] = (tot[m.target] || 0) + m.count; });
    return [...(data?.targets || [])].sort((a, b) => (tot[b.brand] || 0) - (tot[a.brand] || 0) || a.brand.localeCompare(b.brand, 'tr'));
  }, [data, matrix]);
  const platforms = useMemo(() => {
    const set = new Set(matrix.flatMap((m) => m.platforms || []));
    return PLATFORM_ORDER.filter((p) => set.has(p));
  }, [matrix]);
  const sel = target || targets[0]?.brand;
  const selT = targets.find((t) => t.brand === sel);
  const q = query.trim().toLocaleLowerCase('tr');
  const chipTargets = q
    ? targets.filter((t) => t.brand.toLocaleLowerCase('tr').includes(q))
    : [...targets.slice(0, 12), ...(selT && !targets.slice(0, 12).includes(selT) ? [selT] : [])];
  const rowTargets = showAllRows ? targets : [...targets.slice(0, 20), ...(selT && !targets.slice(0, 20).includes(selT) ? [selT] : [])];
  const mobPlat = plat || platforms[0];

  const cols = useMemo(() => {
    const tot = {};
    matrix.forEach((m) => { tot[m.alternative] = (tot[m.alternative] || 0) + m.count; });
    return Object.entries(tot).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'tr')).slice(0, 15).map((x) => x[0]);
  }, [matrix]);
  const groupOf = useMemo(() => Object.fromEntries(matrix.map((m) => [m.alternative, m.parentGroup])), [matrix]);
  const cell = useMemo(() => {
    const m = {};
    matrix.forEach((x) => { m[`${x.target}|${x.alternative}`] = x.count; });
    return m;
  }, [matrix]);

  if (loading && !data) return <TabSkeleton />;
  if (error) return <TabError error={error} retry={retry} />;
  if (!targets.length) return <Card foot={false} icon={Info} tone="gray" title="Alternatif verisi yok" sub="Bu proje için alternatif soruları henüz çalıştırılmamış." />;

  const altsFor = (p) => matrix.filter((m) => m.target === sel && (m.platforms || []).includes(p))
    .sort((a, b) => b.count - a.count || a.alternative.localeCompare(b.alternative, 'tr'));

  return (
    <>
      <Card icon={Repeat} tone="purple" title="Bir marka yerine ne önerildi"
        sub="Markayı seç; her platform kullanıcıya hangi alternatifleri önerdi, görünce karşılaştır.">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Marka ara (${targets.length} marka)`}
            className="w-full sm:w-64 text-sm border border-gray-200/80 rounded-xl px-3 py-1.5 bg-white shadow-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none" />
          {!q && <span className="text-xs text-gray-400">En çok alternatif sorulan ilk 12 marka gösteriliyor.</span>}
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {chipTargets.length === 0 && <span className="text-sm text-gray-400 py-1.5">Eşleşen marka yok.</span>}
          {chipTargets.map((t) => (
            <button key={t.brand} onClick={() => setTarget(t.brand)}
              className={`text-sm font-medium px-3 py-1.5 rounded-xl border shadow-sm ${t.brand === sel ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200/80 text-gray-700 hover:bg-gray-50'}`}>
              {t.brand}
            </button>
          ))}
        </div>
        {selT && <div className="rounded-xl bg-gray-50 border border-gray-200/70 px-3.5 py-2.5 text-sm text-gray-700 mb-4"><span className="text-xs text-gray-500 mr-1.5">Sorulan:</span>{selT.text}</div>}
        <div className="md:hidden mb-4">
          <PlatSeg value={mobPlat} onChange={setPlat} options={platforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p], logo: p }))} />
        </div>
        <div>
          <div className="flex flex-col md:flex-row gap-4 md:gap-5">
            {platforms.map((p) => {
              const list = altsFor(p);
              return (
                <div key={p} className={`min-w-0 md:flex-1 ${p === mobPlat ? 'block' : 'hidden'} md:block`}>
                  <div className="hidden md:flex items-center gap-2 mb-3 pb-2 border-b border-gray-100 text-sm font-semibold text-gray-800">
                    <PlatformLogo p={p} size="w-5 h-5" />{PLATFORM_LABEL[p]}
                  </div>
                  {list.length === 0
                    ? <div className="text-sm text-gray-400 rounded-xl border border-dashed border-gray-200 py-6 text-center">Alternatif önermedi</div>
                    : (
                      <ol className="space-y-1.5">
                        {list.map((a, i) => (
                          <li key={a.alternative} className="flex items-center gap-2.5 text-sm">
                            <span className="w-5 text-right text-xs text-gray-400">{i + 1}</span>
                            <span className="font-medium text-gray-800">{a.alternative}</span>
                            <DrugTag group={a.parentGroup} />
                          </li>
                        ))}
                      </ol>
                    )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      <Card icon={Grid3x3} title="Kim kimin alternatifi"
        sub="Satır: yerine alternatif sorulan marka. Sütun: önerilen alternatif. Hücre: kaç platform andı (0-3). Markanın kendisi alternatif sayılmaz.">
        <HScroll>
            <table className="border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-white text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-2 sm:px-3 py-2 min-w-[104px] sm:min-w-[140px]">Sorulan marka</th>
                  {cols.map((c) => <th key={c} className="text-[11px] font-semibold text-gray-600 px-0 py-2 text-center align-bottom w-[66px] min-w-[66px] leading-tight">{c}{groupOf[c] === 'ilaç' && <span className="block mt-0.5"><DrugTag group="ilaç" /></span>}</th>)}
                </tr>
              </thead>
              <tbody>
                {rowTargets.map((t) => (
                  <tr key={t.brand}>
                    <td className={`sticky left-0 z-10 px-2 sm:px-3 py-1 border-t border-gray-100 whitespace-nowrap text-[13px] sm:text-sm ${t.brand === sel ? 'bg-blue-50' : 'bg-white'}`}>
                      <button onClick={() => setTarget(t.brand)} className="text-sm font-medium text-gray-800 hover:text-blue-700">{t.brand}</button>
                    </td>
                    {cols.map((c) => {
                      const v = t.brand === c ? null : (cell[`${t.brand}|${c}`] || 0);
                      return (
                        <td key={c} className="border-t border-gray-100 p-0.5">
                          <div className="h-9 rounded-lg flex items-center justify-center text-sm font-semibold"
                            style={v == null ? { background: '#f3f4f6', color: '#d1d5db' } : heatStyle(v, 3)}>{v == null ? '' : (v || '')}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
        </HScroll>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {targets.length > 20 && (
            <button onClick={() => setShowAllRows(!showAllRows)} className="text-sm font-medium text-blue-600 hover:text-blue-800">
              {showAllRows ? 'İlk 20 markayı göster' : `Tümünü göster (${targets.length} marka)`}
            </button>
          )}
          <span className="text-[11px] text-gray-400">En çok önerilen 15 alternatif gösteriliyor. Gri hücre markanın kendisidir.</span>
        </div>
      </Card>
    </>
  );
}

// ---------- Ürünler ----------
const CAT_EXTRA = {
  serum: 'Serum', derma_genel: 'Genel dermokozmetik', gunes_ozel: 'Güneş özel', ct_normal: 'Cilt tipi: normal', ct_hassas: 'Cilt tipi: hassas', ct_karma: 'Cilt tipi: karma',
  ct_yagli: 'Cilt tipi: yağlı', ct_kuru: 'Cilt tipi: kuru',
};
const catLabel = (c) => CATEGORY_TR[c] || CAT_EXTRA[c] || CATEGORY_LABEL?.[c] || cap(String(c || '').replace(/_/g, ' '));
const PROD_PLATFORMS = ['openai', 'gemini', 'claude'];
const colsFor = (n, mobile) => (mobile ? `minmax(0, 1fr) ${'48px '.repeat(n)}52px` : `minmax(${n === 3 ? 220 : 150}px, 1fr) ${'80px '.repeat(n)}64px`);
function useIsMobile() {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 767px)').matches : false);
  const [m, setM] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const f = () => setM(mq.matches);
    mq.addEventListener('change', f);
    return () => mq.removeEventListener('change', f);
  }, []);
  return m;
}
// Mobilde tek sayı sütunu: "all" yalnız toplam, aksi halde seçilen platform
const platsFor = (mobile, mp) => (!mobile ? PROD_PLATFORMS : mp === 'all' ? [] : [mp]);
const ProdPlatSeg = ({ value, onChange }) => (
  <div className="md:hidden mb-3">
    <PlatSeg value={value} onChange={onChange} iconOnlyMobile
      options={[{ value: 'all', label: 'Toplam' }, ...PROD_PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABEL[p], logo: p }))]} />
  </div>
);
const pctTxt = (v) => (v == null ? '–' : `%${Math.round(v * 100)}`);

function PlatBar({ n, max, mentioned }) {
  return (
    <div className="px-1" title={mentioned ? `${n} öneri, ${mentioned} kez adı geçti` : `${n} öneri`}>
      <div className={`text-sm font-medium ${n ? 'text-gray-800' : 'text-gray-300'}`}>{n || 0}</div>
      <div className="h-1 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${max ? Math.min(100, (n / max) * 100) : 0}%` }} /></div>
    </div>
  );
}

function ProductHead({ plats }) {
  const mobile = useIsMobile();
  return (
    <div className="grid items-end text-[11px] font-semibold text-gray-500 uppercase tracking-wider pb-2 border-b border-gray-100" style={{ gridTemplateColumns: colsFor(plats.length, mobile) }}>
      <div className="sticky left-0 bg-white z-10 pr-2">Ürün</div>
      {plats.map((p) => <div key={p} className="px-1 inline-flex items-center" title={PLATFORM_LABEL[p]}><PlatformLogo p={p} size="w-4 h-4" /></div>)}
      <div className="px-1">{mobile ? 'Top.' : 'Toplam'}</div>
    </div>
  );
}

function ProductDetail({ pr, personas }) {
  const [more, setMore] = useState(false);
  const ex = pr.examples || [];
  const shown = more ? ex : ex.slice(0, 3);
  const prices = (pr.prices || []).filter((x) => x && x !== 'null');
  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200/70 p-4 my-1.5 space-y-3 md:sticky md:left-0 min-w-0">
      {pr.byIntent && Object.values(pr.byIntent).some((v) => v?.rec) && (
        <div>
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Hangi soru türünde</div>
          <div className="flex flex-wrap gap-1.5">
            {[['genel', 'Genel'], ['persona', 'Persona'], ['fiyat', 'Fiyat bandı'], ['alternatif', 'Alternatif']].filter(([k]) => pr.byIntent[k]?.rec).map(([k, l]) => (
              <Pill key={k} tone={k === 'fiyat' || k === 'alternatif' ? 'amber' : 'gray'}>{l} {pr.byIntent[k].rec}</Pill>
            ))}
          </div>
        </div>
      )}
      {(pr.topCategories || []).length > 0 && (
        <div>
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Hangi kategorilerde</div>
          <div className="flex flex-wrap gap-1.5">{pr.topCategories.map((c) => <Pill key={c.category} tone="blue">{catLabel(c.category)} {c.n}</Pill>)}</div>
        </div>
      )}
      {(pr.personas || []).length > 0 && (
        <div>
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Hangi personalarda</div>
          <div className="flex flex-wrap gap-1.5">
            {pr.personas.map((k) => { const ps = personas.find((x) => x.key === k) || { key: k, name: k }; return (
              <span key={k} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full pr-2.5 py-0.5 pl-0.5 text-xs text-gray-700"><PersonaAvatar p={ps} size="w-6 h-6" />{ps.name}</span>
            ); })}
          </div>
        </div>
      )}
      {prices.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Söylenen fiyatlar</div>
          <ul className="text-xs text-gray-700 space-y-0.5">{prices.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </div>
      )}
      {ex.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Örnek cevap cümleleri</div>
          <div className="space-y-2">
            {shown.map((e, i) => (
              <div key={i} className="flex gap-2 text-xs">
                <PlatformLogo p={e.platform} size="w-4 h-4" />
                <div className="min-w-0">
                  <div className="text-gray-400 break-words">{e.text}</div>
                  <div className="text-gray-800 break-words">{e.snippet}</div>
                </div>
              </div>
            ))}
          </div>
          {ex.length > 3 && (
            <button onClick={() => setMore(!more)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800">
              {more ? <>Daralt <ChevronUp className="w-3.5 h-3.5" /></> : <>Devamını göster ({ex.length - 3}) <ChevronDown className="w-3.5 h-3.5" /></>}
            </button>
          )}
        </div>
      )}
      {pr.catalogUrl && <a href={pr.catalogUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"><ExternalLink className="w-3 h-3" />Katalog sayfası</a>}
    </div>
  );
}

function ProductRow({ pr, max, open, onToggle, personas, tag, plats }) {
  const mobile = useIsMobile();
  const isSeri = pr.level === 'seri';
  const onlyMention = !isSeri && pr.rec === 0;
  return (
    <>
      <button onClick={onToggle} className={`grid items-center w-full text-left py-2 border-b border-gray-100 hover:bg-gray-50/70 ${onlyMention ? 'opacity-60' : ''}`} style={{ gridTemplateColumns: colsFor(plats.length, mobile) }}>
        <div className="md:sticky md:left-0 md:bg-white md:z-10 pr-2 min-w-0">
          <div className="flex items-start gap-1.5">
            <ChevronRight className={`w-3.5 h-3.5 text-gray-400 shrink-0 mt-1 transition ${open ? 'rotate-90' : ''}`} />
            <div className="min-w-0">
              {tag && <div className="mb-0.5">{tag}</div>}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-gray-800 break-words">{pr.name}{isSeri && <span className="text-gray-400 font-normal"> (seri olarak)</span>}</span>
                <DrugTag group={pr.isDrug ? 'ilaç' : null} />
                {onlyMention && <span className="text-[11px] text-gray-500">yalnız adı geçti</span>}
              </div>
            </div>
          </div>
          {pr.shelfPrice != null && <div className="text-[11px] text-gray-400 pl-5">Raf fiyatı {tl(Math.round(pr.shelfPrice))} TL</div>}
        </div>
        {plats.map((p) => <PlatBar key={p} n={pr.byPlatform?.[p]?.rec || 0} mentioned={pr.byPlatform?.[p]?.mentioned} max={max} />)}
        <div className="px-1 text-sm font-semibold text-gray-900">{pr.rec}{pr.mentioned > 0 && <span className="text-[11px] font-normal text-gray-400"> +{pr.mentioned}</span>}</div>
      </button>
      {open && <ProductDetail pr={pr} personas={personas} />}
    </>
  );
}

function BrandProducts({ brandId, brandInfo, personas, scope }) {
  const { data, loading, error, retry } = useApi(`products/brand/${brandInfo.brandId}${scope === 'all' ? '?scope=all' : ''}`, brandId);
  const [open, setOpen] = useState({});
  const [unmOpen, setUnmOpen] = useState(false);
  const mobile = useIsMobile();
  const [mp, setMp] = useState('all');
  const plats = platsFor(mobile, mp);
  const prods = useMemo(() => data?.products || [], [data]);
  const groups = useMemo(() => {
    const m = new Map();
    prods.forEach((x) => { const k = x.line || '__none'; if (!m.has(k)) m.set(k, []); m.get(k).push(x); });
    return [...m.entries()].map(([k, list]) => ({
      key: k, title: k === '__none' ? 'Seri belirtilmemiş' : k,
      total: list.reduce((a, x) => a + x.rec, 0),
      list: [...list].sort((a, b) => (b.level === 'seri') - (a.level === 'seri') || (a.rec === 0) - (b.rec === 0) || b.rec - a.rec),
    })).sort((a, b) => (a.key === '__none') - (b.key === '__none') || b.total - a.total);
  }, [prods]);
  const max = Math.max(1, ...prods.flatMap((x) => PROD_PLATFORMS.map((p) => x.byPlatform?.[p]?.rec || 0)));
  if (loading && !data) return <TabSkeleton />;
  if (error) return <TabError error={error} retry={retry} />;
  if (!prods.length) return <div className="text-sm text-gray-400 py-8 text-center">Bu marka için ürün kaydı yok.</div>;
  return (
    <div>
      <ProdPlatSeg value={mp} onChange={setMp} />
      <HScroll fit={mobile}>
        <div style={{ minWidth: mobile ? 0 : 520 }}>
          <ProductHead plats={plats} />
          {groups.map((g) => (
            <div key={g.key} className="mt-3">
              <div className="flex items-baseline gap-2 py-1.5 sticky left-0 w-fit">
                <span className="text-sm font-semibold text-gray-900">{g.title}</span>
                <span className="text-xs text-gray-400">{g.total} öneri</span>
              </div>
              {g.list.map((pr) => (
                <ProductRow key={pr.productId} pr={pr} max={max} personas={personas} plats={plats} open={!!open[pr.productId]}
                  onToggle={() => setOpen((o) => ({ ...o, [pr.productId]: !o[pr.productId] }))} />
              ))}
            </div>
          ))}
        </div>
      </HScroll>
      {(data.unmatched || []).length > 0 && (
        <div className="mt-5 rounded-xl border border-gray-200/80">
          <button onClick={() => setUnmOpen(!unmOpen)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-gray-600">
            <span>Eşleşmeyen yazımlar ({data.unmatched.length})</span>{unmOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {unmOpen && <ul className="px-3 pb-3 space-y-1 text-xs text-gray-700">{data.unmatched.map((u, i) => <li key={i} className="flex justify-between gap-3"><span className="break-words min-w-0">{u.rawText}</span><span className="text-gray-400 shrink-0">{u.n}</span></li>)}</ul>}
        </div>
      )}
    </div>
  );
}

function CategoryProducts({ brandId, personas }) {
  const catsApi = useApi('products/categories', brandId);
  const groups = useMemo(() => {
    const list = Array.isArray(catsApi.data) ? catsApi.data : null;
    if (!list || !list.length) {
      return [{ world: 'all', label: 'Kategoriler', items: Object.keys(CATEGORY_TR).map((k) => ({ key: k, label: CATEGORY_TR[k] })) }];
    }
    const m = new Map();
    list.forEach((c) => {
      const w = c.world || 'diger';
      if (!m.has(w)) m.set(w, { world: w, label: c.worldLabel || 'Diğer', items: [] });
      m.get(w).items.push({ key: c.key, label: c.label || catLabel(c.key) });
    });
    return [...m.values()];
  }, [catsApi.data]);
  const [cat, setCat] = useState(null);
  const current = cat || (groups.flatMap((g) => g.items).find((i) => i.key === 'akne') || groups[0]?.items[0])?.key || 'akne';
  const curWorld = (groups.find((g) => g.items.some((i) => i.key === current)) || groups[0])?.world;
  const { data, loading, error, retry } = useApi(`products/category/${current}`, brandId);
  const prods = useMemo(() => {
    const l = data?.products || [];
    return [...l.filter((x) => x.rec > 0), ...l.filter((x) => x.rec === 0)];
  }, [data]);
  const max = Math.max(1, ...prods.flatMap((x) => PROD_PLATFORMS.map((p) => x.byPlatform?.[p]?.rec || 0)));
  const nb = data?.brandRecWithoutProduct;
  const [open, setOpen] = useState({});
  const mobile = useIsMobile();
  const [mp, setMp] = useState('all');
  const plats = platsFor(mobile, mp);
  return (
    <Card icon={Package} tone="green" title="Kategoriye göre ürünler" sub="Seçilen kategoride markalar arası en çok önerilen 25 ürün.">
      <div className="md:hidden space-y-2 mb-4">
        {groups.length > 1 && (
          <select value={curWorld} onChange={(e) => { const g = groups.find((x) => x.world === e.target.value); if (g) { setCat(g.items[0].key); setOpen({}); } }}
            className="w-full text-sm border border-gray-200/80 rounded-xl px-3 py-2 bg-white shadow-sm font-medium text-gray-700">
            {groups.map((g) => <option key={g.world} value={g.world}>{g.label}</option>)}
          </select>
        )}
        <select value={current} onChange={(e) => { setCat(e.target.value); setOpen({}); }}
          className="w-full text-sm border border-blue-200 rounded-xl px-3 py-2 bg-blue-50 shadow-sm font-semibold text-blue-800">
          {(groups.find((g) => g.world === curWorld) || groups[0]).items.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div className="hidden md:block space-y-3 mb-5">
        {groups.map((g) => (
          <div key={g.world} className="flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-3">
            {groups.length > 1 && <div className="sm:w-44 shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider sm:pt-2">{g.label}</div>}
            <div className="flex flex-wrap gap-2">
              {g.items.map((c) => (
                <button key={c.key} onClick={() => { setCat(c.key); setOpen({}); }}
                  className={`text-sm font-medium px-3 py-1.5 rounded-xl border shadow-sm ${c.key === current ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200/80 text-gray-700 hover:bg-gray-50'}`}>{c.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {loading && !data ? <TabSkeleton /> : error ? <TabError error={error} retry={retry} /> : prods.length === 0
        ? <div className="text-sm text-gray-400 py-8 text-center">Bu kategoride ürün kaydı yok.</div> : (
          <>
            <ProdPlatSeg value={mp} onChange={setMp} />
            <HScroll fit={mobile}>
              <div style={{ minWidth: mobile ? 0 : 560 }}>
                <ProductHead plats={plats} />
                {prods.map((pr) => (
                  <ProductRow key={pr.productId} pr={pr} max={max} personas={personas} plats={plats} open={!!open[pr.productId]}
                    onToggle={() => setOpen((o) => ({ ...o, [pr.productId]: !o[pr.productId] }))}
                    tag={<span className="inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 bg-blue-50 text-blue-700 whitespace-nowrap">{pr.brand}</span>} />
                ))}
              </div>
            </HScroll>
            <p className="mt-2 text-[11px] text-gray-400">Toplam yanındaki +N: öneri değil, yalnız adı geçti sayısı. Öneri sayısı 0 olan ürünler listenin sonunda, gri.</p>
            <p className="mt-1 text-[11px] text-gray-400">Kapsam sabit: genel ve persona soruları (fiyat ve alternatif soruları hariç).</p>
          </>
        )}
      {nb && nb.ratio != null && (
        <p className="mt-3 text-xs text-gray-500">Bu kategoride marka önerildi ama ürün söylenmedi: {pctTxt(nb.ratio)} ({nb.withoutProduct}/{nb.brandRecs} öneri).</p>
      )}
    </Card>
  );
}

const SKIN_TR = {
  ct_normal: 'Normal cilt', ct_kuru: 'Kuru cilt', ct_yagli: 'Yağlı cilt', ct_karma: 'Karma cilt', ct_hassas: 'Hassas cilt',
  ct_akne: 'Akneye eğilimli cilt', ct_atopik: 'Atopik cilt', ct_rozasea: 'Rozasea', ct_susuz: 'Susuz (dehidre) cilt',
};
const NEED_TR = { temizleyici: 'Temizleyici', nemlendirici: 'Nemlendirici', gunes: 'Güneş kremi', serum: 'Serum', rutin: 'Rutin' };

function SkinItem({ it }) {
  return (
    <div className="min-w-0">
      <span className="text-sm font-medium text-gray-800">{it.brand}</span>
      <DrugTag group={it.isDrug ? 'ilaç' : null} />
      {it.product ? <div className="text-xs text-gray-500 break-words">{it.product}</div> : <div className="text-[11px] text-gray-400">ürün söylenmedi</div>}
    </div>
  );
}

function SkinCell({ cell, platforms }) {
  const q = (cell?.questions || [])[0];
  if (!q) return <div className="text-xs text-gray-300 py-2">Soru yok</div>;
  return (
    <div className="space-y-2.5">
      {platforms.map((p) => {
        const items = q.byPlatform?.[p]?.items || [];
        const first = items[0];
        return (
          <div key={p} className="flex gap-2">
            <PlatformLogo p={p} size="w-4 h-4" />
            <div className="min-w-0 flex-1">
              {first ? <SkinItem it={first} /> : <span className="text-xs text-gray-400">Marka önermedi</span>}
              {items.length > 1 && <div className="text-[11px] text-gray-400">+{items.length - 1} ürün daha</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SkinMatrix({ brandId }) {
  const { data, loading, error, retry } = useApi('products/skin-matrix', brandId);
  const [sel, setSel] = useState(null);
  const rows = useMemo(() => data?.rows || [], [data]);
  const needs = data?.needs || [];
  const platforms = (data?.platforms || PROD_PLATFORMS).filter((p) => PROD_PLATFORMS.includes(p));
  const cur = rows.find((r) => r.skinType === sel) || rows[0];
  // Serum sütununda marka içeren (platform x cilt tipi) hücre oranı
  const serumShare = useMemo(() => {
    let tot = 0; let withBrand = 0;
    rows.forEach((r) => {
      const q = (r.cells?.serum?.questions || [])[0];
      if (!q) return;
      (data?.platforms || PROD_PLATFORMS).forEach((p) => { tot += 1; if ((q.byPlatform?.[p]?.items || []).some((i) => i.brand)) withBrand += 1; });
    });
    return tot ? withBrand / tot : null;
  }, [rows, data]);
  if (loading && !data) return <TabSkeleton />;
  if (error) return <TabError error={error} retry={retry} />;
  if (!rows.length) return <Card foot={false} icon={Info} tone="gray" title="Cilt tipi verisi yok" sub="Cilt tipine göre sorular henüz çalıştırılmamış." />;
  return (
    <Card icon={Grid3x3} tone="purple" title="Cilt tipine göre ürün ihtiyacı"
      sub="Her hücre, o cilt tipi ve ihtiyaç için sorulan tek soruda her platformun ilk önerdiği ürün. Yön gösterir.">
      {serumShare != null && serumShare < 0.3 && <p className="text-xs text-gray-500 -mt-3 mb-4">Serum sorularında platformlar çoğunlukla marka yerine etken madde öneriyor.</p>}
      <div className="md:hidden">
        <div className="flex flex-wrap gap-2 mb-4">
          {rows.map((r) => (
            <button key={r.skinType} onClick={() => setSel(r.skinType)}
              className={`text-sm font-medium px-3 py-1.5 rounded-xl border shadow-sm ${r.skinType === cur.skinType ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200/80 text-gray-700'}`}>{SKIN_TR[r.skinType] || catLabel(r.skinType)}</button>
          ))}
        </div>
        <div className="space-y-3">
          {needs.map((n) => (
            <div key={n} className="rounded-2xl border border-gray-200/80 p-4">
              <div className="text-sm font-semibold text-gray-900 mb-2">{NEED_TR[n] || cap(n)}</div>
              <SkinCell cell={cur.cells?.[n]} platforms={platforms} />
            </div>
          ))}
        </div>
      </div>
      <div className="hidden md:block">
        <HScroll fit>
          <table className="border-separate border-spacing-0 w-full table-fixed" style={{ minWidth: 120 + needs.length * 200 }}>
            <colgroup>
              <col style={{ width: 120 }} />
              {needs.map((n) => <col key={n} />)}
            </colgroup>
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-white text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-3 py-2 w-[120px]">Cilt tipi</th>
                {needs.map((n) => <th key={n} className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-3 py-2">{NEED_TR[n] || cap(n)}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.skinType}>
                  <td className="sticky left-0 z-10 bg-white align-top px-3 py-3 border-t border-gray-100 text-sm font-semibold text-gray-800">{SKIN_TR[r.skinType] || catLabel(r.skinType)}</td>
                  {needs.map((n) => <td key={n} className="align-top px-3 py-3 border-t border-gray-100"><SkinCell cell={r.cells?.[n]} platforms={platforms} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </HScroll>
      </div>
    </Card>
  );
}

function ProductsTab({ brandId }) {
  const [scope, setScope] = useState('default');
  const brandsApi = useApi(scope === 'all' ? 'products/brands?scope=all' : 'products/brands', brandId);
  const personasApi = useApi('personas', brandId);
  const personas = useMemo(() => (Array.isArray(personasApi.data?.personas) ? personasApi.data.personas : personasApi.data?.personas?.personas) || [], [personasApi.data]);
  const [view, setView] = useState('marka');
  const [sel, setSel] = useState(null);
  const [query, setQuery] = useState('');
  const brands = useMemo(() => [...(brandsApi.data || [])].sort((a, b) => b.recMentions - a.recMentions), [brandsApi.data]);
  const cur = brands.find((b) => b.brandId === sel) || brands.find((b) => /roche/i.test(b.brand)) || brands[0];
  const q = query.trim().toLocaleLowerCase('tr');
  const top12 = brands.slice(0, 12);
  const chips = q ? brands.filter((b) => b.brand.toLocaleLowerCase('tr').includes(q)) : [...top12, ...(cur && !top12.includes(cur) ? [cur] : [])];
  if (brandsApi.loading && !brandsApi.data) return <TabSkeleton />;
  if (brandsApi.error) return <TabError error={brandsApi.error} retry={brandsApi.retry} />;
  if (!brands.length) return <Card foot={false} icon={Info} tone="gray" title="Ürün verisi yok" sub="Ürün eşleştirmesi henüz yapılmamış." />;
  return (
    <div className="space-y-6">
      <PlatSeg value={view} onChange={setView} options={[{ value: 'marka', label: 'Marka görünümü', short: 'Marka' }, { value: 'kategori', label: 'Kategori görünümü', short: 'Kategori' }, { value: 'cilt', label: 'Cilt tipine göre', short: 'Cilt tipi' }]} />
      {view === 'cilt' ? <SkinMatrix brandId={brandId} /> : view === 'kategori' ? <CategoryProducts brandId={brandId} personas={personas} /> : (
        <Card icon={Package} tone="green" title="Markanın altında hangi ürün öneriliyor"
          sub="Markayı seç; ürünler seri bazında gruplanır, satıra tıklayınca nerede ve kime önerildiği açılır.">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Marka ara (${brands.length} marka)`}
              className="w-full sm:w-64 text-sm border border-gray-200/80 rounded-xl px-3 py-1.5 bg-white shadow-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none" />
            {!q && <span className="text-xs text-gray-400">En çok önerilen ilk 12 marka gösteriliyor.</span>}
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {chips.length === 0 && <span className="text-sm text-gray-400 py-1.5">Eşleşen marka yok.</span>}
            {chips.map((b) => (
              <button key={b.brandId} onClick={() => setSel(b.brandId)}
                className={`text-sm font-medium px-3 py-1.5 rounded-xl border shadow-sm ${b.brandId === cur?.brandId ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200/80 text-gray-700 hover:bg-gray-50'}`}>{b.brand}</button>
            ))}
          </div>
          {cur && (
            <>
              <div className="rounded-2xl bg-blue-50/60 border border-blue-100 px-4 py-3 mb-5">
                <p className="text-sm text-gray-800">
                  <span className="font-semibold">{cur.brand}</span> {cur.recMentions} kez önerildi; bunların <span className="font-semibold">{pctTxt(cur.productShare)}</span>'inde ürün adı da söylendi.
                </p>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
                  {PROD_PLATFORMS.filter((p) => cur.platforms?.[p]).map((p) => (
                    <span key={p} className="inline-flex items-center gap-1.5"><PlatformLogo p={p} size="w-3.5 h-3.5" />{PLATFORM_LABEL[p]}: {cur.platforms[p].rec} öneri, {cur.platforms[p].withProduct} ürünlü</span>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-xs text-gray-500">Kapsam:</span>
                <PlatSeg value={scope} onChange={setScope} options={[{ value: 'default', label: 'Genel ve persona soruları', short: 'Genel + persona' }, { value: 'all', label: 'Tüm soru türleri (fiyat ve alternatif dahil)', short: 'Tüm türler' }]} />
              </div>
              <BrandProducts key={`${cur.brandId}-${scope}`} brandId={brandId} brandInfo={cur} personas={personas} scope={scope} />
            </>
          )}
        </Card>
      )}
    </div>
  );
}
